import React, { useEffect, useReducer, useRef } from 'react';
import styled from 'styled-components';
import { Poppins } from 'src/common/Poppins';
import { Spacer } from 'src/common/Spacer';
import { RefreshSvg } from 'src/assets/dynamicSvg';
import { LoaderDots } from '../comps/FetchingState';
import { useRiskIntel } from '../useRiskIntel';
import { useScrollOnReveal } from '../useScrollOnReveal';
import { initialWizardState, wizardReducer } from '../wizardReducer';
import { NarrativeBanner } from '../comps/NarrativeBanner';
import { CharCounter } from '../comps/CharCounter';
import { RiskDescriptionCard, RiskDescriptionCardHandle, MAX_INPUT } from '../comps/RiskDescriptionCard';
import { ResponsesPager } from '../comps/ResponsesPager';
import { EmptyStateCard } from '../comps/EmptyStateCard';
import { SuggestedScenarioCard } from '../comps/SuggestedScenarioCard';
import { ResultBanner } from '../comps/ResultBanner';
import { SecondaryButton } from '../comps/SuggestionButtons';
import { AiValidationState } from '../types';

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 308px;
  background: ${({ theme }) => theme.colors.cultured};
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
`;

// Shared outer border so a warning/invalid banner blends into the card below as one card.
const Merged = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  overflow: hidden;
`;

// "RISK DESCRIPTION" label on the left, character counter top-right (same layout as the review-form fields).
const DescriptionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

// Action row under the red "not a risk" banner — there's no suggestion to accept, only Try again.
// Mirrors the suggestion card's footer (a compact, left-aligned secondary button), not a full-width bar.
const InvalidActions = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.white};
  padding: 16px 20px;
`;

interface NarrativeStepProps {
  workspaceId?: string;
  /** Reports "Try again after accept" mode (on the placeholder post-accept) so the parent can hide the
   *  now-stale frequency/consequence lanes. Pure FE — the BE state is untouched. */
  onReattemptingChange?: (reattempting: boolean) => void;
}

export const NarrativeStep: React.FC<NarrativeStepProps> = ({ workspaceId, onReattemptingChange }) => {
  const { session, pending, busy, startSession, refine, selectCycle } = useRiskIntel();
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState);
  const descRef = useRef<RiskDescriptionCardHandle>(null);

  const cycles = session?.cycles ?? [];
  const cycleCount = cycles.length;
  const hasSelected = !!session?.selectedCycleId;
  const selectedCycle = cycles.find((cycle) => cycle.id === session?.selectedCycleId);

  // A new attempt landed (cycles grew) → consume the placeholder and jump to the newest cycle's slot.
  // Also covers resume/reload: a session that loads with cycles snaps to its latest result.
  const prevCycleCount = useRef(0);
  useEffect(() => {
    if (cycleCount > prevCycleCount.current) dispatch({ type: 'CYCLE_LANDED', page: cycleCount - 1 });
    prevCycleCount.current = cycleCount;
  }, [cycleCount]);

  // Start over (session cleared) → back to a single blank placeholder.
  const prevSessionRef = useRef(session);
  useEffect(() => {
    if (prevSessionRef.current && !session) {
      dispatch({ type: 'RESET' });
      prevCycleCount.current = 0;
    }
    prevSessionRef.current = session;
  }, [session]);

  // Slot model: every analysed cycle (success OR failure) is a slot, plus one trailing empty
  // placeholder while active. Available before AND after accept — Try again on an accepted scenario
  // keeps the responses list and just appends a fresh placeholder, same as before accepting.
  const placeholderActive = state.placeholderActive;
  const totalSlots = Math.max(1, cycleCount + (placeholderActive ? 1 : 0));
  // Once accepted (and no fresh placeholder open) pin to the selected cycle; otherwise follow the pager.
  const selectedIndex = cycles.findIndex((c) => c.id === session?.selectedCycleId);
  const page =
    hasSelected && !placeholderActive ? Math.max(0, selectedIndex) : Math.min(state.page, totalSlots - 1);
  const isPlaceholderSlot = placeholderActive && page === cycleCount;
  const currentCycle = isPlaceholderSlot ? undefined : cycles[page];

  // Re-attempting = on a fresh placeholder after having accepted. While true, the parent hides the
  // (stale) frequency/consequence lanes; navigating back to the accepted slot shows them again.
  useEffect(() => {
    onReattemptingChange?.(hasSelected && isPlaceholderSlot);
  }, [hasSelected, isPlaceholderSlot, onReattemptingChange]);
  useEffect(() => () => onReattemptingChange?.(false), [onReattemptingChange]);

  const analysing = pending && !state.accepting && !hasSelected;

  // Disable Analyse on a duplicate input — re-submitting text identical to a previous attempt is a no-op.
  const trimmedInput = state.narrativeInput.trim();
  const isDuplicate =
    trimmedInput.length > 0 && cycles.some((c) => c.userInput.trim().toLowerCase() === trimmedInput.toLowerCase());
  const canAnalyse = isPlaceholderSlot && trimmedInput.length > 0 && !analysing && !isDuplicate;

  // Focus the textarea when a fresh placeholder opens via Try again (not on initial mount).
  const focusPlaceholder = useRef(false);
  useEffect(() => {
    if (isPlaceholderSlot && focusPlaceholder.current) {
      focusPlaceholder.current = false;
      descRef.current?.focus();
    }
  }, [isPlaceholderSlot, page]);

  // Scroll when a NEW attempt lands (keyed on the latest cycle), not on chevron navigation.
  const latest = cycles[cycleCount - 1];
  const resultPhase = hasSelected ? false : analysing ? 'analysing' : latest ? `result-${latest.id}` : false;
  const resultRef = useScrollOnReveal(resultPhase);

  const handleAnalyse = async () => {
    if (!canAnalyse) return;
    if (session) await refine(state.narrativeInput);
    else await startSession(state.narrativeInput, workspaceId);
  };

  // "Try again" opens a fresh empty placeholder (optionally pre-filled, e.g. from "Use this suggestion")
  // and navigates to it. Only ever one trailing placeholder — its index is the current cycle count.
  const handleTryAgain = (input?: string) => {
    focusPlaceholder.current = true;
    dispatch({ type: 'TRY_AGAIN', index: cycleCount, input });
  };

  // Accept the currently-viewed suggestion directly (no confirm step). `accepting` keeps the analysing
  // loader hidden while selectCycle is in flight; the Accept button shows its own loading state.
  const handleAccept = async () => {
    const cycleId = currentCycle?.id;
    if (!cycleId || pending) return;
    dispatch({ type: 'START_ACCEPT' });
    try {
      await selectCycle(cycleId);
    } finally {
      dispatch({ type: 'END_ACCEPT' });
    }
  };

  const descValue = isPlaceholderSlot ? state.narrativeInput : currentCycle?.userInput ?? '';

  const renderBody = () => {
    if (analysing) {
      return (
        <Loading>
          <LoaderDots />
        </Loading>
      );
    }

    // The empty placeholder slot — prompt to type + analyse above.
    if (isPlaceholderSlot) return <EmptyStateCard />;

    const c = currentCycle;
    if (!c) return <EmptyStateCard />;

    if (hasSelected && c.id === selectedCycle?.id && c.risk) {
      return (
        <SuggestedScenarioCard
          risk={c.risk}
          accepted
          // Block Try again while anything's loading (a rung can't be cancelled, so don't orphan it).
          pending={busy}
          onAccept={() => undefined}
          // Keep the responses list and append a fresh placeholder (carrying this input), same as pre-accept.
          onTryAgain={() => handleTryAgain(c.userInput)}
        />
      );
    }

    // A "not a risk" attempt: red banner, no suggestion to accept — only Try again.
    if (c.validation?.state === AiValidationState.Invalid) {
      return (
        <Merged>
          <ResultBanner
            variant="invalid"
            heading={c.validation?.warningMessage ?? 'Description is not a risk'}
            body={
              c.validation?.reason ??
              "We couldn't identify a risk in your description above, so we cannot generate a suggested scenario. Enter a valid risk description above containing a source, event, and consequence."
            }
            suggestion={c.validation?.suggestion}
            onUseSuggestion={c.validation?.suggestion ? () => handleTryAgain(c.validation!.suggestion!) : undefined}
          />
          <InvalidActions>
            <SecondaryButton
              label="Try again"
              icon={<RefreshSvg size={16} />}
              whiteBg
              onClick={() => handleTryAgain(c.userInput)}
            />
          </InvalidActions>
        </Merged>
      );
    }

    if (!c.risk) return <EmptyStateCard />;

    const cardProps = {
      risk: c.risk,
      accepted: false,
      pending,
      onAccept: handleAccept,
      // Carry this attempt's input into the fresh placeholder so the user tweaks rather than retypes.
      onTryAgain: () => handleTryAgain(c.userInput),
    };

    if (c.validation?.state === AiValidationState.Warning) {
      return (
        <Merged>
          <ResultBanner
            variant="warning"
            heading={c.validation?.warningMessage ?? 'Description missing some details'}
            body={
              c.validation?.reason ??
              "This description doesn't have a clear source, event, and consequence. We have generated a suggested scenario based on our best guess of what your description means."
            }
            suggestion={c.validation?.suggestion}
            onUseSuggestion={c.validation?.suggestion ? () => handleTryAgain(c.validation!.suggestion!) : undefined}
          />
          <SuggestedScenarioCard {...cardProps} bare />
        </Merged>
      );
    }

    return <SuggestedScenarioCard {...cardProps} />;
  };

  return (
    <div>
      <NarrativeBanner />
      <Spacer $px={24} />

      <DescriptionHeader>
        <Poppins px={16} weight={600} color="cflowerBlue" css="text-transform: uppercase;">
          Risk Description
        </Poppins>
        <CharCounter value={descValue} max={MAX_INPUT} />
      </DescriptionHeader>
      <Spacer $px={12} />
      <RiskDescriptionCard
        ref={descRef}
        value={descValue}
        onChange={(value) => dispatch({ type: 'SET_INPUT', value })}
        onAnalyse={handleAnalyse}
        analysing={analysing}
        readOnly={!isPlaceholderSlot}
        canAnalyse={canAnalyse}
      />

      <div ref={resultRef}>
        {/* The pager separates the description card from the result and lets you navigate slots. Shown
            whenever there are slots to page (incl. a placeholder opened via Try again after accept).
            On a freshly-accepted scenario with no placeholder there's no pager, so a spacer stands in
            for it — otherwise the two cards butt right up against each other. */}
        {!hasSelected || placeholderActive ? (
          <ResponsesPager
            current={page + 1}
            total={totalSlots}
            onPrev={() => dispatch({ type: 'SET_PAGE', value: page - 1 })}
            onNext={() => dispatch({ type: 'SET_PAGE', value: page + 1 })}
          />
        ) : (
          <Spacer $px={24} />
        )}

        {renderBody()}
      </div>
    </div>
  );
};
